import api from './api';
import { Customer } from './customers';
import { Business } from './businesses';
import { TaskType } from './taskTypes';
import { TaskTemplate } from './taskTemplates';
import { Quotation } from './quotations';
import { Team } from './teams';
import { Staff } from './staffs';

export interface User {
  id: number;
  name: string;
  email: string;
}

export interface SubTaskTemplate {
  id: number;
  name: string;
  task_template_id?: number;
  enabled: boolean;
  taskTemplate?: TaskTemplate;
}

export interface TaskSubTask {
  id?: number | string; // Allow string for temporary items
  task_id: number;
  sub_task_template_id: number;
  name?: string; // Custom name, if not provided, use subTaskTemplate.name
  completed: boolean;
  created_by?: number;
  updated_by?: number;
  created_at?: string;
  updated_at?: string;
  subTaskTemplate?: SubTaskTemplate;
  creator?: User;
  updater?: User;
}

export interface TaskDraftReport {
  id: number;
  task_id: number;
  attachment?: string;
  enabled: boolean;
  created_by?: number;
  created_at: string;
  creator?: User;
}

export interface TaskComment {
  id: number;
  task_id: number;
  user_id?: number;
  comment?: string;
  created_by?: number;
  created_at: string;
  user?: User;
  creator?: User;
}

export interface TaskReview {
  id: number;
  task_id: number;
  customer_id?: number;
  review?: string;
  rating?: number;
  created_by?: number;
  created_at: string;
  customer?: Customer;
  creator?: User;
}

export interface TaskTimelog {
  id: number;
  task_id: number;
  date: string;
  hour: number;
  description?: string;
  enabled: boolean;
  created_at: string;
}

export interface Task {
  id: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  customer_id?: number;
  business_id?: number;
  task_type_id?: number;
  year?: number;
  task_template_id?: number;
  created_date?: string;
  due_date?: string;
  deadline?: string;
  plan_date?: string;
  plan_hour?: number;
  actual_hour?: number;
  quotation_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'Plan' | 'Progress' | 'Review' | 'Approve' | 'Completed';
  is_sent?: boolean;
  is_completed?: boolean;
  enabled: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  business?: Business;
  taskType?: TaskType;
  taskTemplate?: TaskTemplate;
  task_type?: TaskType;
  task_template?: TaskTemplate;
  quotation?: Quotation;
  team?: Team;
  staff?: Staff;
  subTasks?: TaskSubTask[];
  draftReports?: TaskDraftReport[];
  comments?: TaskComment[];
  reviews?: TaskReview[];
  timelogs?: TaskTimelog[];
}

export interface CreateTaskData {
  type?: 'personal' | 'business';
  reference_id?: number;
  customer_id?: number;
  business_id?: number;
  task_type_id?: number;
  year?: number;
  task_template_id?: number;
  created_date?: string;
  due_date?: string;
  deadline?: string;
  plan_date?: string;
  plan_hour?: number;
  actual_hour?: number;
  quotation_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'Plan' | 'Progress' | 'Review' | 'Approve' | 'Completed';
  is_sent?: boolean;
  is_completed?: boolean;
  enabled?: boolean;
  sub_tasks?: Array<{
    sub_task_template_id: number;
    completed?: boolean;
  }>;
}

export interface CreateTaskDraftReportData {
  task_id: number;
  attachment?: File;
  enabled?: boolean;
}

export interface CreateTaskCommentData {
  task_id: number;
  comment: string;
}

export interface CreateTaskReviewData {
  task_id: number;
  customer_id?: number;
  review: string;
  rating?: number;
}

export interface CreateTaskTimelogData {
  task_id: number;
  date: string;
  hour: number;
  description?: string;
  enabled?: boolean;
}

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export const taskService = {
  async getAll() {
    const response = await api.get('/tasks');
    return response.data;
  },

  async getById(id: number): Promise<Task> {
    const response = await api.get(`/tasks/${id}`);
    return unwrap<Task>(response);
  },

  async create(data: CreateTaskData): Promise<Task> {
    const response = await api.post('/tasks', data);
    return unwrap<Task>(response);
  },

  async update(id: number, data: Partial<CreateTaskData>): Promise<Task> {
    const response = await api.put(`/tasks/${id}`, data);
    return unwrap<Task>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/tasks/${id}`);
  },
};

export const taskDraftReportService = {
  async create(data: CreateTaskDraftReportData): Promise<TaskDraftReport> {
    const formData = new FormData();
    formData.append('task_id', data.task_id.toString());
    if (data.attachment) {
      formData.append('attachment', data.attachment);
    }
    formData.append('enabled', (data.enabled ?? true).toString());
    
    const response = await api.post('/task-draft-reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<TaskDraftReport>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-draft-reports/${id}`);
  },
};

export const taskCommentService = {
  async create(data: CreateTaskCommentData): Promise<TaskComment> {
    const response = await api.post('/task-comments', data);
    return unwrap<TaskComment>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-comments/${id}`);
  },
};

export const taskReviewService = {
  async create(data: CreateTaskReviewData): Promise<TaskReview> {
    const response = await api.post('/task-reviews', data);
    return unwrap<TaskReview>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-reviews/${id}`);
  },
};

export const taskTimelogService = {
  async getAll(): Promise<{ data: TaskTimelog[] }> {
    const response = await api.get('/task-timelogs');
    return response.data;
  },

  async create(data: CreateTaskTimelogData): Promise<TaskTimelog> {
    const response = await api.post('/task-timelogs', data);
    return unwrap<TaskTimelog>(response);
  },

  async update(id: number, data: Partial<CreateTaskTimelogData>): Promise<TaskTimelog> {
    const response = await api.put(`/task-timelogs/${id}`, data);
    return unwrap<TaskTimelog>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-timelogs/${id}`);
  },
};
